import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../contexts/authContext';
import { Box, Container, Typography } from '@material-ui/core';
import {
  getAllReleaseForms,
  ReleaseFormData,
} from '../../services/releaseFormsService';
import { Severity } from '../../types';
import useSnackbar from '../../hooks/useSnackbar';
import {
  FFTable,
  FFTableColumn,
  FFTableFilterValue,
} from '../../components/FFTable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { MDButton } from 'components/MDButton';
import { MedicalRecordRow } from './MedicalRecordRow';
import { getSignedUrl } from 'services/storageService';
import TenantSelector from 'components/TenantSelector';

// Use ReleaseFormData directly since we don't need to extend it
type DisplayReleaseFormData = ReleaseFormData;

type ReleaseFormsPageData = {
  entities: DisplayReleaseFormData[];
  isLoading: boolean;
  error: null | Error;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

// Status chip component for displaying the status with appropriate colors
function StatusChip({ value }: { value: string }) {
  let color = '#757575'; // Default grey

  switch (value.toLowerCase()) {
    case 'new':
      color = '#b3b3b3ff'; // Orange
      break;
    case 'processing':
      color = '#fb8c00'; // Orange
      break;
    case 'completed':
      color = '#43a047'; // Green
      break;
    case 'rejected':
      color = '#e53935'; // Red
      break;
    case 'failed_to_process':
      color = '#e53935'; // Red
      break;
  }

  return (
    <span
      style={{
        color: '#fff',
        background: color,
        borderRadius: 8,
        padding: '2px 10px',
        fontWeight: 500,
        fontSize: 13,
        display: 'inline-block',
      }}
    >
      {value.toLowerCase() === 'new' ? 'Not Requested' : value}
    </span>
  );
}

export function ReleaseFormsPage() {
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [untilDate, setUntilDate] = useState<Date | undefined>(undefined);
  const [currentTenant, setCurrentTenant] = useState<number>(0);
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [pageData, setPageData] = useState<ReleaseFormsPageData>({
    entities: [],
    isLoading: true,
    error: null,
    pagination: {
      page: 1,
      pageSize: 100,
      total: 0,
      totalPages: 0,
    },
  });
  const [activeFilters, setActiveFilters] = useState<FFTableFilterValue[]>([]);
  const show = useSnackbar();

  const handleDownload = async (docUrl: string, fileName: string) => {
    if (!docUrl) return;
    const url = await getSignedUrl(docUrl, 'waiver');
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}_hipaa_waiver.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns: FFTableColumn<DisplayReleaseFormData>[] = [
    {
      id: 'caseId',
      label: t('Case ID'),
      width: 120,
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      id: 'policyNumber',
      label: t('Policy Number'),
      width: 120,
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      id: 'uploadedBy',
      label: t('Uploaded By'),
      width: 150,
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      id: 'status',
      label: t('Status'),
      width: 120,
      type: 'text',
      sortable: true,
      filterable: true,
      renderCell: (value) => <StatusChip value={value} />,
    },
    {
      id: 'createdAt',
      label: t('Created At'),
      width: 150,
      type: 'date',
      sortable: true,
    },
    {
      id: 'updatedAt',
      label: t('Updated At'),
      width: 150,
      type: 'date',
      sortable: true,
    },
    {
      id: 'docUrl',
      label: t('HIPAA Waiver'),
      width: 120,
      type: 'text',
      renderCell: (value) => (
        <MDButton
          variant="text"
          color="info"
          size="small"
          onClick={(e) => {
            e.preventDefault();
            handleDownload(value, 'download');
          }}
        >
          {t('Download')}
        </MDButton>
      ),
    },
  ];

  const fetchData = useCallback(
    async (
      page = 1,
      pageSize = 100,
      filterColumn?: string,
      filterValue?: string,
      sortColumn?: string,
      sortOrder?: 'asc' | 'desc'
    ) => {
      try {
        setPageData((prev) => ({ ...prev, isLoading: true }));

        const response = await getAllReleaseForms({
          page,
          pageSize,
          whereKey: filterColumn,
          whereValue: filterValue,
          sortBy: sortColumn,
          sortOrder,
          fromDate,
          untilDate,
          carrierId: currentTenant,
        });

        // Ensure all release forms have a medicalRecords array, even if empty
        const normalizedReleaseForms = response.releaseForms.map((form) => ({
          ...form,
          medicalRecords: form.medicalRecords || [],
        }));

        setPageData({
          entities: normalizedReleaseForms,
          isLoading: false,
          error: null,
          pagination: response.pagination,
        });
      } catch (error) {
        const e = error as Error;
        setPageData((prev) => ({
          ...prev,
          isLoading: false,
          error: e,
        }));
        show('Failed to load release forms data', Severity.error);
      }
    },
    [show, fromDate, untilDate, currentTenant]
  );

  useEffect(() => {
    fetchData();
  }, [currentTenant]); // Added fetchData dependency which now includes currentTenant

  const handlePageChange = useCallback(
    (newPage: number) => {
      fetchData(newPage, pageData.pagination.pageSize);
    },
    [fetchData, pageData.pagination.pageSize]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      fetchData(1, newPageSize);
    },
    [fetchData]
  );

  const handleDateRangeChange = (
    from: Date | undefined,
    until: Date | undefined
  ) => {
    setFromDate(from);
    setUntilDate(until);
  };

  const handleDownloadExcel = async () => {
    try {
      // Fetch all data for export (increase page size)
      const response = await getAllReleaseForms({
        page: 1,
        pageSize: 1000, // Fetch all for export
        fromDate,
        untilDate,
        carrierId: currentTenant,
      });

      // Ensure all release forms have a medicalRecords array, even if empty
      const normalizedReleaseForms = response.releaseForms.map((form) => ({
        ...form,
        medicalRecords: form.medicalRecords || [],
      }));

      // Prepare data for Excel export
      const dataToExport = normalizedReleaseForms.map((form) => ({
        ID: form.id,
        'Case ID': form.caseId,
        'Policy Number': form.policyNumber,
        'Uploaded By': form.uploadedBy,
        Status: form.status,
        'Document URL': form.docUrl,
        'Created At': form.createdAt.toLocaleString(),
        'Updated At': form.updatedAt.toLocaleString(),
      }));

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Release Forms');

      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });
      const blob = new Blob([excelBuffer], {
        type: 'application/octet-stream',
      });
      saveAs(blob, 'release-forms-data.xlsx');
    } catch (error) {
      show('Failed to download Excel file', Severity.error);
    }
  };

  if (!user) {
    return <div>{t('Loading')}..</div>;
  }

  if (user.status === 'new') {
    return (
      <Container>
        <Typography variant="h4" component="p">
          Welcome to FairFare Dashboard!
        </Typography>
        <Typography component="p">
          To activate your account and access all FairFare features please
          contact us
        </Typography>
      </Container>
    );
  }

  if (pageData.isLoading && pageData.entities.length === 0) {
    return <div>{t('Loading')}..</div>;
  }

  if (pageData.error) {
    return (
      <div>
        {t('Error loading release forms data')}: {pageData.error.message}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '100vw', margin: '0 auto', overflow: 'auto' }}>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Box pl={2}>
          <TenantSelector
            value={currentTenant}
            onChange={(newTenantId) => setCurrentTenant(newTenantId)}
            label="Tenant"
          />
        </Box>
        <MDButton variant="gradient" color="info" onClick={handleDownloadExcel}>
          {t('Download as Excel')}
        </MDButton>
      </Box>
      <FFTable
        columns={columns}
        rows={pageData.entities}
        pagination={{
          page: pageData.pagination.page,
          pageSize: pageData.pagination.pageSize,
          total: pageData.pagination.total,
          totalPages: pageData.pagination.totalPages,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
          pageSizeOptions: [50, 100],
        }}
        serverSide={true}
        loading={pageData.isLoading}
        error={pageData.error}
        onFilterChange={(filters: FFTableFilterValue[]) => {
          setActiveFilters(filters);
          if (filters.length === 0) {
            // No filters, reset to first page with current page size
            fetchData(1, pageData.pagination.pageSize);
          } else {
            // Apply filter
            const filter = filters[0]; // We only support one filter at a time
            fetchData(
              1,
              pageData.pagination.pageSize,
              String(filter.columnId),
              filter.value
            );
          }
        }}
        filterColumnsOptions={[
          'caseId',
          'uploadedBy',
          'status',
          'policyNumber',
        ]}
        initialFilters={activeFilters}
        onSortChange={(orderBy, order) => {
          fetchData(
            1,
            pageData.pagination.pageSize,
            undefined,
            undefined,
            orderBy,
            order
          );
        }}
        mandatoryDateRange={{
          fromDate,
          untilDate,
          onDateRangeChange: handleDateRangeChange,
        }}
        expandableRows={{
          isRowExpandable: (row) => row.medicalRecords.length > 0,
          renderExpandedContent: MedicalRecordRow,
        }}
        rowKey="id"
        emptyStateMessage={t('No release form records found')}
        errorStateMessage={t('Error loading release form data')}
        loadingStateMessage={t('Loading release form data...')}
      />
    </div>
  );
}
